import { useEffect, useState } from 'react';

import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetReportingByIdQuery, usePostChartDataMutation, useRunReportMutation } from 'apis/report-templates.api';
import { ButtonVariants } from 'constants/common';
import { DAY_FORMAT_WITH_TIME_WITHOUT_SECONDS_MONTHS, YEAR_FORMAT, YEAR_FORMAT_WITH_MONTHS } from 'constants/global';
import {
  chartTypeMap,
  FILTER_CONDITIONS_FOR_BOOLEANS,
  FILTER_CONDITIONS_FOR_NUMBERS,
  FILTER_CONDITIONS_FOR_STRINGS,
  FILTER_TIMELAPSE_TIME,
  VISUALIZATION_OPTIONS,
} from 'constants/reports-templates';
import { ROUTES } from 'constants/routes';
import { useOpen } from 'hooks/useOpen';
import { VisualizationItemPayload } from 'interfaces/report-templates.interface';
import { CommonError } from 'interfaces/shared.interface';
import {
  BackButton,
  Button,
  Checkbox,
  ErrorModal,
  Input,
  InputNumberFormat,
  LoadingOverlay,
  RangeDatePickerInput,
  SelectFormInput,
} from 'shared-components';
import DatePickerInput from 'shared-components/DatePickerInput';
import SelectFromInput from 'shared-components/select/SelectFormInput';
import { cn } from 'utils/global';

import CreateReportTemplateSchedule from './CreateReportTemplateSchedule';

const RunReportTemplatePage = () => {
  type TimelapseForm = {
    timelapseType: { value: number; label: string };
    dateValue: Date | null;
    dateRange: [Date, Date] | null;
  };
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isOpenErrorModal, handleOnOpenErrorModal, handleOnCloseErrorModal] = useOpen();
  const { data } = useGetReportingByIdQuery(id ?? '', { skip: !id });
  const [getChartData, { data: chartDataResponse, isLoading: isChartLoading, error: chartError }] =
    usePostChartDataMutation();
  const [runReport, { data: tableResponse }] = useRunReportMutation();
  const [timelapseRange, setTimelapseRange] = useState<[Date, Date] | null>(null);
  const { error: errorData } = (chartError as CommonError)?.data || {};
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { control, watch, setValue, getValues } = useForm<TimelapseForm>({
    defaultValues: {
      timelapseType: FILTER_TIMELAPSE_TIME[0],
    },
  });
  const dateRange = watch('dateRange');
  const watchedFilters = watch() as Record<string, any>;
  const [isTimelapseEnabled, setIsTimelapseEnabled] = useState(false);
  const selectedType = watch('timelapseType')?.value;
  const xAxisColumn = data?.result?.xAxisColumn;
  const isTableType = data?.result?.visualizationTypeId === 1;
  const visualizationName =
    typeof data?.result?.visualizationTypeId === 'number'
      ? VISUALIZATION_OPTIONS[data.result.visualizationTypeId - 1]?.value
      : 'Bar';
  const chartJsType = chartTypeMap[visualizationName ?? 'Bar'];
  const datePickerMode: 'year' | 'month' = selectedType === 2 ? 'year' : 'month';
  const datePickerFormat = selectedType === 2 ? YEAR_FORMAT : YEAR_FORMAT_WITH_MONTHS;

  const chartLabels = chartDataResponse?.result?.labels ?? [];
  const chartValues = chartDataResponse?.result?.values ?? [];

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: data?.result?.xAxisColumn?.name,
        data: chartValues,
      },
    ],
  };

  const tableData = tableResponse?.result ?? [];
  const tableColumns = tableData.length
    ? Object.keys(tableData[0]).map((key) => ({
        field: key,
        header: key,
      }))
    : [];

  const getConditionLabel = (columnType: number, condition: number | null) => {
    const map: Record<number, { id: number; label: string }[]> = {
      5: FILTER_CONDITIONS_FOR_NUMBERS,
      4: FILTER_CONDITIONS_FOR_BOOLEANS,
      3: FILTER_CONDITIONS_FOR_NUMBERS,
      2: FILTER_CONDITIONS_FOR_STRINGS,
    };

    return map[columnType]?.find((c) => c.id === condition)?.label ?? '';
  };

  const getFilterType = (type: number): string => {
    switch (type) {
      case 5:
        return 'date';
      case 4:
        return 'boolean';
      case 3:
        return 'number';
      case 2:
        return 'string';
      default:
        return 'unknown';
    }
  };

  const handleRunClick = () => {
    if (!data?.result || !id) {
      return;
    }

    const getTransformedFilters = () => {
      return (data?.result?.filters ?? []).map((filter, index) => {
        let finalValue = filter.value;

        const rhfValue = watchedFilters[`static-value-${getFilterType(filter.columnType)}-${index}`];
        if (rhfValue !== undefined) {
          finalValue =
            typeof rhfValue === 'object' && rhfValue !== null && 'value' in rhfValue ? rhfValue.value : rhfValue;
        }

        if (filter.columnType === 5 && (finalValue as any) instanceof Date) {
          finalValue = (finalValue as any).toISOString();
        }

        return {
          ...filter,
          value: finalValue,
        };
      });
    };

    if (isTableType) {
      runReport({
        id: Number(id),
        filters: data.result.filters ?? [],
      });
    } else {
      if (!xAxisColumn) {
        return;
      }

      const visualizationColumn: VisualizationItemPayload = {
        name: xAxisColumn.name,
        tableName: xAxisColumn.tableName,
        type: xAxisColumn.type,
        displayType: xAxisColumn.displayType != null ? String(xAxisColumn.displayType) : null,
      };

      const valueRange = isTimelapseEnabled
        ? dateRange
        : getDateRangeFromSingleValue(selectedType, getValues('dateValue'));

      const transformedFilters = getTransformedFilters();

      getChartData({
        reportId: Number(id),
        from: valueRange?.[0]?.toISOString(),
        to: valueRange?.[1]?.toISOString(),
        filters: transformedFilters,
        column: visualizationColumn,
        timelapseType: selectedType,
      });
    }
  };

  const getDateRangeFromSingleValue = (type: number, value: Date | null): [Date, Date] | null => {
    if (!value || !(value instanceof Date)) {
      return null;
    }

    const year = value.getFullYear();

    if (type === 1 || type === 2) {
      return [new Date(year, 0, 1), new Date(year, 11, 31, 23, 59, 59, 999)];
    }

    if (type === 3 || type === 4) {
      const month = value.getMonth();
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
      return [start, end];
    }

    return null;
  };

  const filtersButton = data?.result?.xAxisColumn?.type === 5 || data?.result?.yAxisColumn?.type === 5;

  useEffect(() => {
    if (data && id) {
      handleRunClick();
    }
  }, [data, id]);

  useEffect(() => {
    if (isTimelapseEnabled && !timelapseRange) {
      const now = new Date();
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const defaultRange: [Date, Date] = [startOfYear, now];

      setTimelapseRange(defaultRange);
      setValue('dateRange', defaultRange);
    }
  }, [isTimelapseEnabled]);

  useEffect(() => {
    if (chartError) {
      handleOnOpenErrorModal();
    }
  }, [chartError]);

  return (
    <>
      <ErrorModal
        errorMessage={errorData?.message}
        description={errorData?.details}
        isOpen={isOpenErrorModal}
        handleClose={handleOnCloseErrorModal}
      />
      <>
        <div className="flex items-center">
          <BackButton label="" onClick={() => navigate(ROUTES.reportTemplates)} className="m-0 p-0" />
          <p className="ml-2 text-xl">{data?.result?.name}</p>
        </div>
        {filtersButton || data?.result?.filters?.length ? (
          <div>
            <Button className="mb-3" onClick={handleRunClick}>
              {t('Run')}
            </Button>
            <Button variant={ButtonVariants.OUTLINE} onClick={() => setIsFiltersOpen((prev) => !prev)}>
              {t('Filters')} ({(data?.result?.filters?.length ?? 0) + (filtersButton ? 2 : 0)})
            </Button>
            {isFiltersOpen && (
              <div className="absolute z-10 mt-0 bg-white border rounded border-neutral-200 dark:border-neutral-800 p-4 min-w-[250px]">
                {data?.result?.filters?.map((filter, index) => (
                  <div key={filter.id} className="flex gap-2 mb-2 items-center">
                    <Input
                      disabled
                      name={`static-column-${index}`}
                      value={`${filter.tableName} → ${filter.columnName}`}
                      inputClassName="!w-[12rem]"
                    />

                    <Input
                      disabled
                      name={`static-condition-${index}`}
                      value={getConditionLabel(filter.columnType, filter.condition)}
                      inputClassName="!w-[12rem]"
                    />

                    {filter.columnType === 5 && (
                      <DatePickerInput
                        name={`static-value-date-${index}`}
                        control={control as any}
                        defaultValue={format(filter.value, DAY_FORMAT_WITH_TIME_WITHOUT_SECONDS_MONTHS)}
                        format={DAY_FORMAT_WITH_TIME_WITHOUT_SECONDS_MONTHS}
                        inputClassName="!w-[12rem]"
                      />
                    )}
                    {filter.columnType === 4 && (
                      <SelectFormInput
                        control={control as any}
                        name={`static-value-boolean-${index}`}
                        defaultValue={{ label: String(filter.value), value: String(filter.value) }}
                        options={[
                          { label: 'true', value: 'true' },
                          { label: 'false', value: 'false' },
                        ]}
                        inputClassName="!w-[12rem]"
                      />
                    )}
                    {filter.columnType === 3 && (
                      <InputNumberFormat
                        name={`static-value-number-${index}`}
                        control={control as any}
                        value={filter.value}
                        inputClassName="!w-[12rem]"
                      />
                    )}
                    {filter.columnType === 2 && (
                      <Input
                        name={`static-value-string-${index}`}
                        value={filter.value}
                        readOnly
                        inputClassName="!w-[12rem]"
                      />
                    )}
                  </div>
                ))}
                {filtersButton && (
                  <>
                    <div className="flex gap-2 items-center mb-2">
                      <SelectFromInput
                        name="timelapseType"
                        control={control as any}
                        options={FILTER_TIMELAPSE_TIME}
                        placeholder={t('Select type')}
                        inputClassName={cn('w-[19.25rem]', {
                          'w-[9.375rem]': selectedType !== 1,
                          'w-[19.25rem]': isTimelapseEnabled,
                        })}
                        isFilter
                      />

                      {selectedType !== 1 && !isTimelapseEnabled && (
                        <DatePickerInput
                          className="w-full"
                          name="dateValue"
                          control={control as any}
                          inputClassName="w-[9.375rem] h-[2.375rem]"
                          mode={datePickerMode}
                          format={datePickerFormat}
                        />
                      )}
                    </div>

                    <label className="flex items-center mb-2">
                      <Checkbox
                        className="mr-2 my-2"
                        dataCy="select-template-checkbox"
                        checked={isTimelapseEnabled}
                        onChange={() => setIsTimelapseEnabled((prev) => !prev)}
                      />
                      {t('EnableTimelapseDateRange')}
                    </label>
                  </>
                )}

                {isTimelapseEnabled && (
                  <RangeDatePickerInput
                    control={control as any}
                    name="dateRange"
                    placeholder={['StartDate', 'EndDate']}
                    dataCy="filter-date-range"
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          ''
        )}
      </>
      {isChartLoading ? (
        <LoadingOverlay isLoading={isChartLoading} />
      ) : (
        <>
          <div className="p-4">
            <CreateReportTemplateSchedule
              visualizationType={visualizationName}
              chartJsType={chartJsType}
              chartJsData={chartData}
              columns={isTableType ? tableColumns : []}
              tableData={isTableType ? tableData : []}
              tableClassName="!w-full bg-white-00 border-none"
              graphClassName="!bg-white-00 border-none"
            />
          </div>
        </>
      )}
    </>
  );
};

export default RunReportTemplatePage;
